import React, { useState, useEffect } from 'react';
import { app } from './firebase';
import { getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app'; 

const db = getFirestore(app);

export default function CustomersView({ customers, vacancies, loading, isReadOnly, logAction }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  
  // Tabs State
  const [activeTab, setActiveTab] = useState('general'); // 'general', 'kyc', 'portal', 'vacancies'
  
  // Portal Users State
  const [portalUsers, setPortalUsers] = useState([]);
  const [newPortalUser, setNewPortalUser] = useState({ name: '', email: '' });
  const [isAddingUser, setIsAddingUser] = useState(false);

  // --- Filtering ---
  const filteredCustomers = customers.filter(c => {
    if (!c.companyName) return false;
    const text = (c.companyName + " " + c.industry + " " + c.contactName).toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  // --- Listeners for Portal Users ---
  useEffect(() => {
      let unsub;
      if (selectedCustomer && selectedCustomer.id) {
          // Listen to the 'portal_users' subcollection for this customer
          unsub = onSnapshot(collection(db, "customers", selectedCustomer.id, "portal_users"), (snap) => {
              setPortalUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          });
      } else {
          setPortalUsers([]);
      }
      return () => { if(unsub) unsub(); };
  }, [selectedCustomer]);

  // --- Handlers: Customer ---
  const handleSave = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;

    try {
      // Clean undefined data
      const cleanData = { ...formData };
      Object.keys(cleanData).forEach(key => cleanData[key] === undefined && delete cleanData[key]);

      if (formData.id) {
        await updateDoc(doc(db, "customers", formData.id), cleanData);
        logAction("Update Customer", `Updated ${formData.companyName}`);
      } else {
        const ref = await addDoc(collection(db, "customers"), cleanData);
        setFormData({ ...cleanData, id: ref.id }); // Update ID for continued editing
        logAction("Create Customer", `Created ${formData.companyName}`);
      }
      
      setIsEditing(false);
      // Update local view
      if(formData.id) setSelectedCustomer(cleanData);
      else setSelectedCustomer({...cleanData, id: formData.id}); 
      
      alert("Customer saved successfully.");
    } catch(err) {
      console.error("Error saving customer:", err);
      alert("Could not save customer.");
    }
  };

  const handleDelete = async (e, id, companyName) => {
    e.stopPropagation();
    if (isReadOnly) return;
    if (!window.confirm(`Are you sure you want to delete ${companyName}?`)) return;

    try {
      await deleteDoc(doc(db, "customers", id));
      if (selectedCustomer && selectedCustomer.id === id) {
        setSelectedCustomer(null);
      }
      logAction("Delete Customer", `Deleted ${companyName}`);
    } catch (err) {
      console.error("Error deleting customer:", err);
    }
  };

  // --- Handlers: Portal Users ---
  const handleAddClientUser = async (e) => {
      e.preventDefault();
      if(isReadOnly) return;
      if(!newPortalUser.email || !newPortalUser.name) return alert("Fill in name and email.");
      
      setIsAddingUser(true);
      try {
          const functions = getFunctions(getApp(), 'europe-west1');
          const createClientFn = httpsCallable(functions, 'createClientUser');
          
          const result = await createClientFn({
              email: newPortalUser.email,
              name: newPortalUser.name,
              customerId: selectedCustomer.id,
              customerName: selectedCustomer.companyName
          });

          if(result.data.success) {
              alert(`User created! Credentials sent to ${newPortalUser.email}.`);
              setNewPortalUser({ name: '', email: '' });
              logAction("Add Client User", `Added ${newPortalUser.email} to ${selectedCustomer.companyName}`);
          }
      } catch (err) {
          console.error(err);
          if (err.code === 'functions/already-exists') {
              alert("A user account already exists for this email. They can already access their associated portals.");
          } else {
              alert("Error adding user: " + err.message);
          }
      } finally {
          setIsAddingUser(false);
      }
  };

  const handleRemoveClientUser = async (userId, email) => {
      if(isReadOnly) return;
      if(!window.confirm(`Permanently remove access for ${email}? This will delete their account.`)) return;
      
      try {
          // Use Cloud Function to delete Auth account AND DB record
          const functions = getFunctions(getApp(), 'europe-west1');
          const removeClientFn = httpsCallable(functions, 'removeClientUser');
          
          await removeClientFn({
              uid: userId,
              customerId: selectedCustomer.id
          });
          
          logAction("Remove Client User", `Removed ${email} from ${selectedCustomer.companyName}`);
          alert("User removed successfully.");
      } catch(err) {
          console.error(err);
          alert("Error removing user: " + err.message);
      }
  };

  const openModal = (customer) => {
    setSelectedCustomer(customer);
    setFormData(customer || { status: 'Active', country: 'Belgium' });
    setIsEditing(false);
    setActiveTab('general'); // Reset tab
  };

  const openNewModal = () => {
      const newCust = { status: 'Active', country: 'Belgium', type: 'Private Enterprise' };
      setSelectedCustomer(newCust); // Temporary object for modal
      setFormData(newCust);
      setIsEditing(true);
      setActiveTab('general');
  };

  return (
    <div className="container-fluid p-0">
      
      {/* HEADER & SEARCH */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-main">Customers</h2>
          <p className="text-muted-theme">Manage client relationships, KYC, and portal access.</p>
        </div>
        <div className="d-flex gap-2">
          <input 
            type="text" 
            className="form-control"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {!isReadOnly && (
              <button className="btn btn-primary" onClick={openNewModal}>
                <i className="bi bi-plus-lg"></i> Add
              </button>
          )}
        </div>
      </div>

      {/* CARDS GRID */}
      {loading ? (
        <div className="text-main">Loading Customers...</div>
      ) : (
        <div className="row g-4">
          {filteredCustomers.map((c) => (
            <div key={c.id} className="col-md-6 col-lg-4">
              <div 
                className="card h-100 bg-card border-theme shadow-sm"
                style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                onClick={() => openModal(c)}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <div className="bg-primary bg-opacity-10 text-primary p-3 rounded">
                      <i className={`bi ${c.type === 'Government' ? 'bi-bank' : (c.type === 'Family Business' ? 'bi-shop' : 'bi-building')} fs-4`}></i>
                    </div>
                    <div className="d-flex align-items-start gap-2">
                        <span className={`badge ${c.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                            {c.status}
                        </span>
                        {!isReadOnly && (
                            <button 
                                className="btn btn-sm btn-outline-danger border-0" 
                                onClick={(e) => handleDelete(e, c.id, c.companyName)}
                                title="Delete"
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        )}
                    </div>
                  </div>
                  
                  <h4 className="fw-bold mb-1 text-main">{c.companyName}</h4>
                  <p className="text-muted-theme small mb-1">{c.type || 'Private Enterprise'}</p>
                  <p className="text-muted-theme small mb-3">{c.industry}</p>
                  
                  <div className="border-top border-theme pt-3 mt-3">
                    <p className="text-muted-theme small mb-1">
                      <i className="bi bi-person me-2"></i> {c.contactName || 'No Contact'}
                    </p>
                    <p className="text-muted-theme small mb-0">
                      <i className="bi bi-envelope me-2"></i> {c.contactEmail || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL */}
      {selectedCustomer && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              
              <div className="modal-header border-theme">
                <h5 className="modal-title text-main">
                  {isEditing ? (formData.id ? 'Edit Customer' : 'New Customer') : selectedCustomer.companyName}
                </h5>
                <button type="button" className="btn-close" onClick={() => setSelectedCustomer(null)}></button>
              </div>

              {/* TABS */}
              <ul className="nav nav-tabs border-theme px-3 pt-2">
                  <li className="nav-item">
                      <button className={`nav-link ${activeTab === 'general' ? 'active bg-card text-primary' : 'text-muted-theme'}`} onClick={() => setActiveTab('general')}>General Info</button>
                  </li>
                  <li className="nav-item">
                      <button className={`nav-link ${activeTab === 'kyc' ? 'active bg-card text-primary' : 'text-muted-theme'}`} onClick={() => setActiveTab('kyc')}>KYC & Culture</button>
                  </li>
                  {/* Only show Portal & Vacancies tab if customer is saved (has ID) */}
                  {formData.id && (
                      <>
                        <li className="nav-item">
                            <button className={`nav-link ${activeTab === 'portal' ? 'active bg-card text-primary' : 'text-muted-theme'}`} onClick={() => setActiveTab('portal')}>Portal Users</button>
                        </li>
                        <li className="nav-item">
                            <button className={`nav-link ${activeTab === 'vacancies' ? 'active bg-card text-primary' : 'text-muted-theme'}`} onClick={() => setActiveTab('vacancies')}>Vacancies</button>
                        </li>
                      </>
                  )}
              </ul>

              <div className="modal-body p-4">
                
                {/* --- TAB 1: GENERAL INFO --- */}
                {activeTab === 'general' && (
                    <form onSubmit={handleSave}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="text-muted-theme small">Company Name</label>
                                <input className="form-control" value={formData.companyName || ''} onChange={e => setFormData({...formData, companyName: e.target.value})} disabled={!isEditing && !isReadOnly} />
                            </div>
                            <div className="col-md-6">
                                <label className="text-muted-theme small">Industry</label>
                                <input className="form-control" value={formData.industry || ''} onChange={e => setFormData({...formData, industry: e.target.value})} disabled={!isEditing} />
                            </div>
                            
                            <div className="col-md-6">
                                <label className="text-muted-theme small">Contact Name</label>
                                <input className="form-control" value={formData.contactName || ''} onChange={e => setFormData({...formData, contactName: e.target.value})} disabled={!isEditing} />
                            </div>
                            <div className="col-md-6">
                                <label className="text-muted-theme small">Status</label>
                                <select className="form-select" value={formData.status || 'Active'} onChange={e => setFormData({...formData, status: e.target.value})} disabled={!isEditing}>
                                    <option>Active</option>
                                    <option>Lead</option>
                                    <option>Inactive</option>
                                </select>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted-theme small">Email</label>
                                <input type="email" className="form-control" value={formData.contactEmail || ''} onChange={e => setFormData({...formData, contactEmail: e.target.value})} disabled={!isEditing} />
                            </div>
                            <div className="col-md-6">
                                <label className="text-muted-theme small">Phone Number</label>
                                <input type="text" className="form-control" value={formData.contactPhone || ''} onChange={e => setFormData({...formData, contactPhone: e.target.value})} disabled={!isEditing} />
                            </div>

                            <div className="col-12 border-top border-theme pt-3 mt-2">
                                <h6 className="text-main mb-3">Invoicing Details</h6>
                            </div>
                            <div className="col-md-6">
                                <label className="text-muted-theme small">VAT Number</label>
                                <input className="form-control" value={formData.vatNumber || ''} onChange={e => setFormData({...formData, vatNumber: e.target.value})} disabled={!isEditing} />
                            </div>
                            <div className="col-md-6">
                                <label className="text-muted-theme small">Invoicing Email</label>
                                <input className="form-control" value={formData.invoiceEmail || ''} onChange={e => setFormData({...formData, invoiceEmail: e.target.value})} disabled={!isEditing} />
                            </div>
                            <div className="col-12">
                                <label className="text-muted-theme small">Address</label>
                                <input className="form-control" value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} disabled={!isEditing} />
                            </div>
                        </div>
                    </form>
                )}

                {/* --- TAB 2: KYC & CULTURE --- */}
                {activeTab === 'kyc' && (
                    <form onSubmit={handleSave}>
                        <div className="row g-3">
                            <div className="col-12 mb-2">
                                <div className="alert alert-secondary p-2 small border-theme text-muted-theme">
                                    <i className="bi bi-info-circle me-2"></i>
                                    This information helps consultants understand the work environment.
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted-theme small">Organization Type</label>
                                <select className="form-select" value={formData.type || 'Private Enterprise'} onChange={e => setFormData({...formData, type: e.target.value})} disabled={!isEditing}>
                                    <option>Private Enterprise</option>
                                    <option>Family Business</option>
                                    <option>Government</option>
                                    <option>Public Sector</option>
                                    <option>Start-up / Scale-up</option>
                                    <option>Non-Profit</option>
                                </select>
                            </div>
                            
                            <div className="col-md-6">
                                <label className="text-muted-theme small">Company Size</label>
                                <select className="form-select" value={formData.size || '50-200'} onChange={e => setFormData({...formData, size: e.target.value})} disabled={!isEditing}>
                                    <option>1-10 (Micro)</option>
                                    <option>11-50 (Small)</option>
                                    <option>50-200 (Medium)</option>
                                    <option>200-1000 (Large)</option>
                                    <option>1000+ (Corporate)</option>
                                </select>
                            </div>

                            <div className="col-12">
                                <label className="text-muted-theme small">Work Environment (Tags)</label>
                                <input 
                                    className="form-control" 
                                    placeholder="e.g. Remote-first, Formal, Agile, Dutch-speaking" 
                                    value={formData.environmentTags || ''} 
                                    onChange={e => setFormData({...formData, environmentTags: e.target.value})} 
                                    disabled={!isEditing} 
                                />
                            </div>

                            <div className="col-12">
                                <label className="text-muted-theme small">Culture & Values Description</label>
                                <textarea 
                                    className="form-control" 
                                    rows="4" 
                                    placeholder="Describe the atmosphere, dress code, and team dynamics..." 
                                    value={formData.cultureDescription || ''} 
                                    onChange={e => setFormData({...formData, cultureDescription: e.target.value})} 
                                    disabled={!isEditing}
                                ></textarea>
                            </div>

                            <div className="col-12">
                                <label className="text-muted-theme small">Internal KYC Notes (Private)</label>
                                <textarea 
                                    className="form-control" 
                                    rows="3" 
                                    placeholder="Internal risk assessment or notes..." 
                                    value={formData.notes || ''} 
                                    onChange={e => setFormData({...formData, notes: e.target.value})} 
                                    disabled={!isEditing}
                                ></textarea>
                            </div>
                        </div>
                    </form>
                )}

                {/* --- TAB 3: PORTAL USERS --- */}
                {activeTab === 'portal' && formData.id && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="text-main mb-0">Client Portal Access</h6>
                            <span className="badge bg-secondary">{portalUsers.length} Users</span>
                        </div>

                        {/* ADD USER FORM */}
                        {!isReadOnly && (
                            <div className="bg-input p-3 border border-theme rounded mb-4">
                                <h6 className="text-muted-theme small fw-bold mb-2">Invite New User</h6>
                                <div className="row g-2 align-items-end">
                                    <div className="col-5">
                                        <input className="form-control form-control-sm" placeholder="Name" value={newPortalUser.name} onChange={e => setNewPortalUser({...newPortalUser, name: e.target.value})} />
                                    </div>
                                    <div className="col-5">
                                        <input className="form-control form-control-sm" placeholder="Email" value={newPortalUser.email} onChange={e => setNewPortalUser({...newPortalUser, email: e.target.value})} />
                                    </div>
                                    <div className="col-2">
                                        <button className="btn btn-sm btn-primary w-100" onClick={handleAddClientUser} disabled={isAddingUser}>
                                            {isAddingUser ? '...' : 'Add'}
                                        </button>
                                    </div>
                                </div>
                                <small className="text-muted-theme" style={{fontSize: '0.75rem'}}>User will receive an email with login credentials.</small>
                            </div>
                        )}

                        {/* USERS LIST */}
                        <div className="list-group">
                            {portalUsers.length > 0 ? portalUsers.map(u => (
                                <div key={u.id} className="list-group-item bg-card border-theme d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="fw-bold text-main">{u.name}</div>
                                        <div className="small text-muted-theme">{u.email}</div>
                                    </div>
                                    {!isReadOnly && (
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveClientUser(u.id, u.email)} title="Remove Access">
                                            <i className="bi bi-person-dash"></i>
                                        </button>
                                    )}
                                </div>
                            )) : (
                                <p className="text-center text-muted-theme fst-italic py-3">No portal users assigned yet.</p>
                            )}
                        </div>
                    </div>
                )}

                {/* --- TAB 4: VACANCIES --- */}
                {activeTab === 'vacancies' && formData.id && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="text-main mb-0">Linked Vacancies</h6>
                            <span className="badge bg-secondary">
                                {(vacancies || []).filter(v => v.customerId === formData.id).length} Found
                            </span>
                        </div>
                        
                        <div className="list-group">
                            {(vacancies || []).filter(v => v.customerId === formData.id).length > 0 ? (
                                (vacancies || []).filter(v => v.customerId === formData.id)
                                .sort((a,b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                                .map(v => (
                                    <div key={v.id} className="list-group-item bg-card border-theme mb-2 rounded shadow-sm p-3">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <div className="fw-bold text-main">{v.jobTitle}</div>
                                                <div className="small text-muted-theme mb-2">
                                                    <i className="bi bi-geo-alt me-1"></i> {v.region} | {v.status}
                                                </div>
                                                <div className="d-flex gap-1 flex-wrap">
                                                    {v.keywords?.split(',').slice(0, 3).map((k, i) => (
                                                        <span key={i} className="badge bg-secondary bg-opacity-10 text-muted-theme border border-theme" style={{fontSize: '0.65rem'}}>{k.trim()}</span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="text-end">
                                                <div className="badge bg-info text-dark mb-1">{v.candidates?.length || 0} Applicants</div>
                                                <div className="small text-muted-theme" style={{fontSize: '0.7rem'}}>
                                                    {v.createdAt ? new Date(v.createdAt.seconds * 1000).toLocaleDateString() : 'No date'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-5 text-muted-theme">
                                    <i className="bi bi-briefcase fs-1 d-block mb-3"></i>
                                    <p>No vacancies have been created for this customer yet.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

              </div>

              {/* FOOTER ACTIONS */}
              <div className="modal-footer border-theme d-flex justify-content-between">
                  {/* Actions for General/KYC Tabs */}
                  {activeTab !== 'portal' && activeTab !== 'vacancies' ? (
                      <>
                        {!isEditing ? (
                            <button className="btn btn-outline-primary" onClick={() => setIsEditing(true)}>
                                <i className="bi bi-pencil me-2"></i> Edit Details
                            </button>
                        ) : (
                            <button className="btn btn-outline-secondary" onClick={() => {setIsEditing(false); setFormData(selectedCustomer);}}>
                                Cancel Editing
                            </button>
                        )}

                        <div className="d-flex gap-2">
                            {isEditing && <button className="btn btn-success" onClick={handleSave}>Save Changes</button>}
                            {!isEditing && <button className="btn btn-secondary" onClick={() => setSelectedCustomer(null)}>Close</button>}
                        </div>
                      </>
                  ) : (
                      // Actions for Portal & Vacancies Tab
                      <button className="btn btn-secondary ms-auto" onClick={() => setSelectedCustomer(null)}>Close</button>
                  )}
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}